import React, { useMemo, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { analyzeTalentRecommendations, generateTrajectoryPredictions } from '../../../services/aiAnalysisService';
import { 
  fetchTrendingTalentRankings, 
  subscribeToPlayerProfileChanges, 
  unsubscribeFromChannel 
} from '../../../services/realtimeService';

const TrendingTalent = ({ players, coaches, goalkeepers, filterRole }) => {
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [showTrajectories, setShowTrajectories] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [trajectories, setTrajectories] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [loadingTrajectories, setLoadingTrajectories] = useState(false);
  const [error, setError] = useState(null);
  const [trendingRankings, setTrendingRankings] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);

  // Fetch trending rankings from database
  useEffect(() => {
    loadTrendingRankings();

    // Subscribe to real-time player profile changes
    const channel = subscribeToPlayerProfileChanges(() => {
      loadTrendingRankings();
    });

    return () => {
      unsubscribeFromChannel(channel);
    };
  }, []);

  const loadTrendingRankings = async () => {
    setLoadingTrending(true);
    try {
      const { data, error: fetchError } = await fetchTrendingTalentRankings(20);
      if (fetchError) {
        console.error('Error loading trending rankings:', fetchError);
      } else {
        setTrendingRankings(data || []);
      }
    } catch (err) {
      console.error('Error loading trending rankings:', err);
    } finally {
      setLoadingTrending(false);
    }
  };

  const topPlayers = useMemo(() => {
    if (filterRole !== 'all' && filterRole !== 'player') return [];
    return players
      ?.map(p => ({
        ...p,
        score: (p?.goalsScored || 0) * 3 + (p?.assists || 0) * 2 + (p?.motmAwards || 0) * 5
      }))
      ?.sort((a, b) => b?.score - a?.score)
      ?.slice(0, 5);
  }, [players, filterRole]);

  const topCoaches = useMemo(() => {
    if (filterRole !== 'all' && filterRole !== 'coach') return [];
    const coachesWithWinRate = coaches?.map(c => {
      const totalGames = c?.totalGamesManaged || 0;
      const winRate = totalGames > 0 ? ((c?.gamesWon || 0) / totalGames) * 100 : 0;
      return { ...c, winRate };
    });
    return coachesWithWinRate
      ?.sort((a, b) => b?.winRate - a?.winRate)
      ?.slice(0, 5);
  }, [coaches, filterRole]);

  const topGoalkeepers = useMemo(() => {
    if (filterRole !== 'all' && filterRole !== 'goalkeeper') return [];
    return goalkeepers
      ?.map(g => {
        const cleanSheetRate = (g?.gamesPlayed || 0) > 0 ? ((g?.cleanSheets || 0) / (g?.gamesPlayed || 0)) * 100 : 0;
        return { ...g, cleanSheetRate };
      })
      ?.sort((a, b) => b?.cleanSheetRate - a?.cleanSheetRate)
      ?.slice(0, 5);
  }, [goalkeepers, filterRole]);

  const fetchAIRecommendations = async () => {
    setLoadingRecommendations(true);
    setError(null);
    try {
      const result = await analyzeTalentRecommendations(players);
      if (result?.error) {
        setError(result?.error);
        setShowAIRecommendations(false);
      } else {
        setAiRecommendations(result?.recommendations || []);
      }
    } catch (err) {
      setError('Failed to generate AI recommendations');
      setShowAIRecommendations(false);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const fetchTrajectories = async () => {
    setLoadingTrajectories(true);
    setError(null);
    try {
      const result = await generateTrajectoryPredictions(players);
      if (result?.error) {
        setError(result?.error);
        setShowTrajectories(false);
      } else {
        setTrajectories(result?.predictions || []);
      }
    } catch (err) {
      setError('Failed to generate trajectory predictions');
      setShowTrajectories(false);
    } finally {
      setLoadingTrajectories(false);
    }
  };

  useEffect(() => {
    if (showAIRecommendations && aiRecommendations?.length === 0 && !loadingRecommendations) {
      fetchAIRecommendations();
    }
  }, [showAIRecommendations]);

  useEffect(() => {
    if (showTrajectories && trajectories?.length === 0 && !loadingTrajectories) {
      fetchTrajectories();
    }
  }, [showTrajectories]);

  const getRankColor = (index) => {
    if (index === 0) return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30';
    if (index === 1) return 'bg-gray-400/20 text-gray-600 border-gray-400/30';
    if (index === 2) return 'bg-orange-500/20 text-orange-600 border-orange-500/30';
    return 'bg-muted text-muted-foreground border-border';
  };

  const getTrajectoryColor = (trajectory) => {
    if (trajectory?.toLowerCase()?.includes('rising') || trajectory?.toLowerCase()?.includes('upward')) {
      return 'bg-success/10 text-success';
    } else if (trajectory?.toLowerCase()?.includes('stable') || trajectory?.toLowerCase()?.includes('steady')) {
      return 'bg-blue-500/10 text-blue-600';
    } else {
      return 'bg-warning/10 text-warning';
    }
  };

  const renderTrendingRankings = () => {
    if (loadingTrending) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (trendingRankings?.length === 0) {
      return (
        <div className="text-center py-8">
          <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">No trending talent data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Live Trending Rankings</h3>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-success font-medium">LIVE</span>
          </div>
        </div>
        {trendingRankings?.slice(0, 10)?.map((player, index) => (
          <div key={player?.id} className="flex items-center gap-4 p-4 rounded-lg bg-card border border-border hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 ${getRankColor(index)}`}>
              #{index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-foreground truncate">
                {player?.fullName || player?.knownAs || 'Unknown Player'}
              </h4>
              <p className="text-xs text-muted-foreground truncate">
                {player?.currentClub || 'No club'} • {player?.primaryPosition || 'N/A'}
              </p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Icon name="TrendingUp" size={12} color="var(--color-success)" />
                  <span className="text-xs font-medium text-success">
                    {player?.marketValueChange30d > 0 ? '+' : ''}
                    {player?.marketValueChange30d ? `€${(player?.marketValueChange30d / 1000000)?.toFixed(1)}M` : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Target" size={12} color="var(--color-primary)" />
                  <span className="text-xs text-muted-foreground">
                    {player?.totalGoals || 0}G / {player?.totalAssists || 0}A
                  </span>
                </div>
                {player?.recentTransfersCount > 0 && (
                  <div className="flex items-center gap-1">
                    <Icon name="ArrowRightLeft" size={12} color="var(--color-warning)" />
                    <span className="text-xs text-warning font-medium">
                      {player?.recentTransfersCount} transfer{player?.recentTransfersCount > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-muted-foreground mb-1">Market Value</p>
              <p className="text-sm font-bold text-primary">
                {player?.currentMarketValue ? `€${(player?.currentMarketValue / 1000000)?.toFixed(1)}M` : 'N/A'}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Live Trending Rankings Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        {renderTrendingRankings()}
      </div>

      {/* AI Controls */}
      {(filterRole === 'all' || filterRole === 'player') && players?.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg border border-border">
          <Icon name="Sparkles" size={20} className="text-primary" />
          <span className="text-sm font-medium text-foreground">AI-Powered Analysis:</span>
          <Button
            onClick={() => {
              setShowAIRecommendations(!showAIRecommendations);
              if (!showAIRecommendations && aiRecommendations?.length === 0) {
                fetchAIRecommendations();
              }
            }}
            variant={showAIRecommendations ? 'primary' : 'outline'}
            size="sm"
            disabled={loadingRecommendations}
          >
            <Icon name="Target" size={16} />
            Talent Recommendations
          </Button>
          <Button
            onClick={() => {
              setShowTrajectories(!showTrajectories);
              if (!showTrajectories && trajectories?.length === 0) {
                fetchTrajectories();
              }
            }}
            variant={showTrajectories ? 'primary' : 'outline'}
            size="sm"
            disabled={loadingTrajectories}
          >
            <Icon name="TrendingUp" size={16} />
            Career Trajectories
          </Button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600">
            <Icon name="AlertCircle" size={20} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* AI Talent Recommendations */}
      {showAIRecommendations && (filterRole === 'all' || filterRole === 'player') && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Sparkles" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground">AI Talent Recommendations</h2>
              <p className="text-sm text-muted-foreground">AI-driven analysis of top talent opportunities</p>
            </div>
            {aiRecommendations?.length > 0 && (
              <Button onClick={fetchAIRecommendations} variant="outline" size="sm" disabled={loadingRecommendations}>
                <Icon name="RefreshCw" size={16} />
                Refresh
              </Button>
            )}
          </div>

          {loadingRecommendations ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground">Analyzing talent data...</p>
              </div>
            </div>
          ) : aiRecommendations?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {aiRecommendations?.map((rec, index) => (
                <div
                  key={index}
                  className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${getRankColor(index)}`}>
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-bold text-foreground">{rec?.playerName}</h3>
                        <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                          <Icon name="Star" size={12} />
                          {rec?.rating?.toFixed(1)}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">{rec?.position}</p>
                      <p className="text-sm text-foreground mb-3">{rec?.reasoning}</p>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Strengths:</p>
                          <div className="flex flex-wrap gap-1">
                            {rec?.strengths?.map((strength, idx) => (
                              <span key={idx} className="px-2 py-1 bg-success/10 text-success rounded text-xs">
                                {strength}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Recommendation:</p>
                          <p className="text-sm text-foreground">{rec?.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-card rounded-lg border border-border">
              <p className="text-muted-foreground">No recommendations available</p>
            </div>
          )}
        </div>
      )}

      {/* AI Career Trajectories */}
      {showTrajectories && (filterRole === 'all' || filterRole === 'player') && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-secondary" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground">Career Trajectory Predictions</h2>
              <p className="text-sm text-muted-foreground">AI-powered career path analysis and predictions</p>
            </div>
            {trajectories?.length > 0 && (
              <Button onClick={fetchTrajectories} variant="outline" size="sm" disabled={loadingTrajectories}>
                <Icon name="RefreshCw" size={16} />
                Refresh
              </Button>
            )}
          </div>

          {loadingTrajectories ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground">Predicting career trajectories...</p>
              </div>
            </div>
          ) : trajectories?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {trajectories?.map((traj, index) => (
                <div
                  key={index}
                  className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-foreground mb-1">{traj?.playerName}</h3>
                        <p className="text-sm text-muted-foreground">{traj?.currentLevel}</p>
                      </div>
                      <div className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                        <Icon name="Zap" size={12} />
                        {(traj?.confidence * 100)?.toFixed(0)}% confidence
                      </div>
                    </div>

                    <div className={`p-3 rounded-lg ${getTrajectoryColor(traj?.trajectory)}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="TrendingUp" size={16} />
                        <p className="text-sm font-medium">Trajectory: {traj?.trajectory}</p>
                      </div>
                      <p className="text-xs">Predicted Level: {traj?.predictedLevel}</p>
                      <p className="text-xs mt-1">Timeframe: {traj?.timeframe}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Key Factors:</p>
                      <ul className="space-y-1">
                        {traj?.keyFactors?.map((factor, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                            <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                            <span>{factor}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-card rounded-lg border border-border">
              <p className="text-muted-foreground">No trajectory predictions available</p>
            </div>
          )}
        </div>
      )}

      {/* Top Players */}
      {(filterRole === 'all' || filterRole === 'player') && topPlayers?.length > 0 && !showAIRecommendations && !showTrajectories && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Top Performing Players</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topPlayers?.map((player, index) => (
              <div
                key={player?.id}
                className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${getRankColor(index)}`}>
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">{player?.fullName}</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {player?.position} • {player?.currentTeam}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Goals</p>
                        <p className="text-xl font-bold text-foreground">{player?.goalsScored || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Assists</p>
                        <p className="text-xl font-bold text-foreground">{player?.assists || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">MOTM</p>
                        <p className="text-xl font-bold text-foreground">{player?.motmAwards || 0}</p>
                      </div>
                    </div>
                    {player?.availableForTransfer && (
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
                        <Icon name="TrendingUp" size={14} />
                        Available for Transfer
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Coaches */}
      {(filterRole === 'all' || filterRole === 'coach') && topCoaches?.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Icon name="Clipboard" size={20} className="text-secondary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Top Performing Coaches</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topCoaches?.map((coach, index) => (
              <div
                key={coach?.id}
                className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${getRankColor(index)}`}>
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">{coach?.fullName}</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {coach?.coachingLicense} • {coach?.presentClub}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Win Rate</p>
                        <p className="text-xl font-bold text-success">{coach?.winRate?.toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Games</p>
                        <p className="text-xl font-bold text-foreground">{coach?.totalGamesManaged || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Wins</p>
                        <p className="text-xl font-bold text-foreground">{coach?.gamesWon || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Goalkeepers */}
      {(filterRole === 'all' || filterRole === 'goalkeeper') && topGoalkeepers?.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Top Performing Goalkeepers</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topGoalkeepers?.map((goalkeeper, index) => (
              <div
                key={goalkeeper?.id}
                className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${getRankColor(index)}`}>
                    #{index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">{goalkeeper?.fullName}</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Goalkeeper • {goalkeeper?.currentTeam}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Clean Sheets</p>
                        <p className="text-xl font-bold text-success">{goalkeeper?.cleanSheets || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Games</p>
                        <p className="text-xl font-bold text-foreground">{goalkeeper?.gamesPlayed || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">CS Rate</p>
                        <p className="text-xl font-bold text-foreground">{goalkeeper?.cleanSheetRate?.toFixed(1)}%</p>
                      </div>
                    </div>
                    {goalkeeper?.availableForTransfer && (
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
                        <Icon name="TrendingUp" size={14} />
                        Available for Transfer
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {topPlayers?.length === 0 && topCoaches?.length === 0 && topGoalkeepers?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="TrendingUp" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Trending Talent</h3>
          <p className="text-muted-foreground">No performance data available for the selected filter.</p>
        </div>
      )}
    </div>
  );
};

export default TrendingTalent;